import React, { useState, useRef, useEffect } from 'react';
import './GrammarRulesExercise.css';

const SECTIONS_META = [
  { key: 'introduction', icon: '📘', label: 'Introduction', placeholder: 'Brief overview of the grammar point...' },
  { key: 'formation', icon: '🔧', label: 'Formation Rules', placeholder: 'Clear step-by-step explanation with examples...' },
  { key: 'usage', icon: '💡', label: 'Usage Guidelines', placeholder: 'When and how to use this grammar...' },
  { key: 'examples', icon: '✏️', label: 'Common Examples', placeholder: 'Practical examples in English with context...' },
  { key: 'mistakes', icon: '⚠️', label: 'Common Mistakes', placeholder: 'Typical errors students make with corrections...' },
  { key: 'tips', icon: '🎯', label: 'Practice Tips', placeholder: 'How students can master this grammar point...' },
  { key: 'reference', icon: '📋', label: 'Quick Reference', placeholder: 'Summary table or key points...' },
];

// Extract the HTML under each <h2> heading in the saved content into named
// sections, matching on English/Russian keywords in the heading text.
const parseContentToFields = (htmlContent) => {
  const empty = { introduction: '', formation: '', usage: '', examples: '', mistakes: '', tips: '', reference: '' };
  if (!htmlContent) return empty;

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlContent;

  const sections = { ...empty };
  const headings = tempDiv.querySelectorAll('h2');
  headings.forEach(heading => {
    const headingText = heading.textContent.toLowerCase();
    let sectionContent = '';
    let nextElement = heading.nextElementSibling;
    while (nextElement && nextElement.tagName !== 'H2') {
      sectionContent += nextElement.outerHTML;
      nextElement = nextElement.nextElementSibling;
    }
    sectionContent = sectionContent.trim();

    if (headingText.includes('introduction') || headingText.includes('введение')) sections.introduction = sectionContent;
    else if (headingText.includes('formation') || headingText.includes('правила') || headingText.includes('образование')) sections.formation = sectionContent;
    else if (headingText.includes('usage') || headingText.includes('использование') || headingText.includes('применение')) sections.usage = sectionContent;
    else if (headingText.includes('example') || headingText.includes('пример')) sections.examples = sectionContent;
    else if (headingText.includes('mistake') || headingText.includes('ошибк')) sections.mistakes = sectionContent;
    else if (headingText.includes('tip') || headingText.includes('совет') || headingText.includes('практик')) sections.tips = sectionContent;
    else if (headingText.includes('reference') || headingText.includes('справочн') || headingText.includes('кратк')) sections.reference = sectionContent;
  });

  return sections;
};

const SECTION_HEADINGS = {
  introduction: 'Introduction',
  formation: 'Formation Rules',
  usage: 'Usage Guidelines',
  examples: 'Common Examples',
  mistakes: 'Common Mistakes',
  tips: 'Practice Tips',
  reference: 'Quick Reference',
};

const buildContentFromFields = (fields) => SECTIONS_META
  .map(({ key }) => `<h2>${SECTION_HEADINGS[key]}</h2>\n<div>${fields[key] || ''}</div>`)
  .join('\n\n');

const GrammarRulesExercise = ({
  content: initialContent, title, level, onClose, updateAttributes, isStudent = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editableTitle, setEditableTitle] = useState(title);
  const [editableLevel, setEditableLevel] = useState(level);
  const [fields, setFields] = useState(() => parseContentToFields(initialContent));
  const [rawContent, setRawContent] = useState(initialContent || '');
  // Whether the content actually parsed into any of the known structured
  // sections. Content that doesn't (e.g. freshly-generated content whose
  // headings don't match parseContentToFields' keyword list) would show 7
  // EMPTY boxes with no connection to the real text — and saving from
  // there would silently REPLACE that real content with empty sections.
  // Frozen at the moment edit mode is entered (see handleEnterEdit) so it
  // doesn't flip mid-edit; edit mode falls back to a single raw editor
  // when this is false, seeded with (and saving back) the actual content
  // untouched instead of the 7-section structured editor.
  const [isStructuredEdit, setIsStructuredEdit] = useState(() => {
    // No content yet (a brand new exercise) still gets the friendly
    // 7-section structure to fill in — the raw fallback is only for
    // EXISTING content that doesn't match it, not empty content.
    if (!initialContent) return true;
    return SECTIONS_META.some(({ key }) => (parseContentToFields(initialContent)[key] || '').trim());
  });

  const fieldRefs = useRef({});
  const rawEditorRef = useRef(null);

  // Re-parse whenever the saved content changes (e.g. a peer's edit lands),
  // but never while this side is actively editing — that would blow away
  // whatever's mid-edit here.
  useEffect(() => {
    if (isEditing) return;
    setFields(parseContentToFields(initialContent));
    setRawContent(initialContent || '');
  }, [initialContent, isEditing]);

  // Seed the contentEditable DOM content once when entering edit mode —
  // after that, onInput is the source of truth, so this must not re-run
  // on every keystroke (that would fight the browser's own cursor
  // position).
  useEffect(() => {
    if (!isEditing) return;
    if (isStructuredEdit) {
      SECTIONS_META.forEach(({ key }) => {
        const el = fieldRefs.current[key];
        if (el) el.innerHTML = fields[key] || '';
      });
    } else if (rawEditorRef.current) {
      rawEditorRef.current.innerHTML = rawContent;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  const handleEnterEdit = () => {
    const hasStructuredContent = SECTIONS_META.some(({ key }) => (fields[key] || '').trim());
    setIsStructuredEdit(hasStructuredContent || !rawContent.trim());
    setIsEditing(true);
  };

  const getActiveFieldKey = () => {
    const activeElement = document.activeElement;
    for (const { key } of SECTIONS_META) {
      const el = fieldRefs.current[key];
      if (el && (el === activeElement || el.contains(activeElement))) return key;
    }
    return null;
  };

  const applyFormatting = (command) => {
    let activeField;
    let commitField;
    if (isStructuredEdit) {
      let activeKey = getActiveFieldKey();
      if (!activeKey) {
        activeKey = 'introduction';
        fieldRefs.current[activeKey]?.focus();
      }
      activeField = fieldRefs.current[activeKey];
      commitField = (html) => setFields(prev => ({ ...prev, [activeKey]: html }));
    } else {
      activeField = rawEditorRef.current;
      commitField = (html) => setRawContent(html);
    }
    if (!activeField) return;

    activeField.focus();
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    const selectedText = range.toString();
    if (!selectedText) return;

    let success = false;
    switch (command) {
      case 'bold': success = document.execCommand('bold', false, null); break;
      case 'italic': success = document.execCommand('italic', false, null); break;
      case 'underline': success = document.execCommand('underline', false, null); break;
      case 'code': {
        range.deleteContents();
        range.insertNode(document.createRange().createContextualFragment(`<code>${selectedText}</code>`));
        success = true;
        break;
      }
      case 'highlight': {
        range.deleteContents();
        range.insertNode(document.createRange().createContextualFragment(`<mark>${selectedText}</mark>`));
        success = true;
        break;
      }
      default: return;
    }

    if (success) commitField(activeField.innerHTML);
  };

  const handleFieldInput = (key, e) => {
    setFields(prev => ({ ...prev, [key]: e.target.innerHTML }));
  };

  const handleRawInput = (e) => {
    setRawContent(e.target.innerHTML);
  };

  const handleSave = () => {
    setIsEditing(false);
    if (updateAttributes) {
      updateAttributes({
        content: isStructuredEdit ? buildContentFromFields(fields) : rawContent,
        title: editableTitle,
        level: editableLevel,
      });
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditableTitle(title);
    setEditableLevel(level);
    setFields(parseContentToFields(initialContent));
    setRawContent(initialContent || '');
  };

  const visibleSections = SECTIONS_META.filter(({ key }) => (fields[key] || '').trim());

  return (
    <div className="gr-exercise">
      <div className="gr-header">
        <div className="gr-header-left">
          <div className="gr-header-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
            </svg>
          </div>
          {isEditing ? (
            <input
              type="text"
              value={editableTitle}
              onChange={e => setEditableTitle(e.target.value)}
              className="gr-title-input"
              placeholder="Title"
            />
          ) : (
            <span className="gr-title">{title}</span>
          )}
          {isEditing ? (
            <input
              type="text"
              value={editableLevel}
              onChange={e => setEditableLevel(e.target.value)}
              className="gr-level-input"
              placeholder="Level"
            />
          ) : (
            <span className="gr-level-badge">{level}</span>
          )}
        </div>
        <div className="gr-header-actions">
          {!isEditing && !isStudent && (
            <button className="gr-icon-btn" aria-label="Edit" data-tip="Edit" onClick={handleEnterEdit}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
          )}
          {isEditing && (
            <>
              <button className="gr-icon-btn" aria-label="Save" data-tip="Save" onClick={handleSave}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="9,12 11,14 15,10" />
                </svg>
              </button>
              <button className="gr-icon-btn" aria-label="Cancel" data-tip="Cancel" onClick={handleCancel}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </>
          )}
          {!isEditing && onClose && (
            <button className="gr-icon-btn" aria-label="Close" data-tip="Close" onClick={onClose}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="eye-icon">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" className="eye-open" />
                <path d="M2 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" className="eye-closed" style={{ opacity: 0 }} />
                <circle cx="12" cy="12" r="3" className="eye-pupil" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="gr-body">
        {isEditing ? (
          <>
            <div className="gr-toolbar">
              <button type="button" onClick={() => applyFormatting('bold')} className="gr-toolbar-btn" title="Bold"><strong>B</strong></button>
              <button type="button" onClick={() => applyFormatting('italic')} className="gr-toolbar-btn" title="Italic"><em>I</em></button>
              <button type="button" onClick={() => applyFormatting('underline')} className="gr-toolbar-btn" title="Underline"><u>U</u></button>
              <button type="button" onClick={() => applyFormatting('code')} className="gr-toolbar-btn" title="Code"><code>C</code></button>
              <button type="button" onClick={() => applyFormatting('highlight')} className="gr-toolbar-btn gr-toolbar-btn-highlight" title="Highlight">H</button>
            </div>

            {isStructuredEdit ? (
              SECTIONS_META.map(({ key, icon, label, placeholder }) => (
                <div className="gr-field" key={key}>
                  <label className="gr-field-label"><span className="gr-field-icon">{icon}</span>{label}</label>
                  <div
                    ref={el => { fieldRefs.current[key] = el; }}
                    contentEditable
                    data-field={key}
                    className="gr-rich-editor"
                    onInput={e => handleFieldInput(key, e)}
                    data-placeholder={placeholder}
                  />
                </div>
              ))
            ) : (
              // This content didn't parse into the known sections above —
              // editing it as 7 disconnected empty boxes would silently
              // wipe it out on save (see isStructuredEdit). Edit the raw
              // content directly instead.
              <div className="gr-field">
                <label className="gr-field-label"><span className="gr-field-icon">📝</span>Content</label>
                <div
                  ref={rawEditorRef}
                  contentEditable
                  className="gr-rich-editor gr-raw-editor"
                  onInput={handleRawInput}
                  data-placeholder="Grammar rule content..."
                />
              </div>
            )}
          </>
        ) : visibleSections.length > 0 ? (
          <div className="gr-sections">
            {visibleSections.map(({ key, icon, label }) => (
              <div className="gr-section-card" key={key}>
                <div className="gr-section-header">
                  <span className="gr-section-icon">{icon}</span>
                  <span className="gr-section-title">{label}</span>
                </div>
                <div className="gr-section-body" dangerouslySetInnerHTML={{ __html: fields[key] }} />
              </div>
            ))}
          </div>
        ) : initialContent ? (
          // Fallback for content that doesn't parse into the known h2
          // sections above (e.g. freshly AI-generated content whose
          // headings don't match parseContentToFields' keyword list) —
          // show it as-is instead of silently rendering nothing, matching
          // this exercise's original always-show-the-raw-content behavior.
          <div
            className="gr-section-body gr-raw-content"
            dangerouslySetInnerHTML={{ __html: initialContent.replace(/<\s*h1[^>]*>[\s\S]*?<\s*\/\s*h1\s*>/gi, '') }}
          />
        ) : (
          <div className="gr-empty">No content yet.</div>
        )}
      </div>
    </div>
  );
};

export default GrammarRulesExercise;
